import { useEffect } from "react";
import { ArrowLeft, ArrowUpRight, Compass, Lightbulb, Rocket, Anchor, Clock, Route, Blocks } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { CONTACT_EMAIL } from "@/i18n/contactDetails";

const PRESENTATIE_IMG = "/images/presentatie-cbs.jpg";

const H2 = ({ children }) => (
    <h2 className="font-display text-3xl tracking-tight text-bone md:text-4xl">{children}</h2>
);

const AANPAK = [
    {
        n: "01",
        icon: Compass,
        title: "Verkennen",
        body: "We gaan bij je aan tafel en nemen je processen en cijfers door — zonder aannames, zonder jargon. Wat kost je de meeste tijd, waar lekt marge, waar zit de groei?",
    },
    {
        n: "02",
        icon: Lightbulb,
        title: "Adviseren",
        body: "Je krijgt een helder advies: wat te verbeteren, in welke volgorde, en wat het je concreet oplevert in uren en euro's. Geen dik rapport dat in een la verdwijnt.",
    },
    {
        n: "03",
        icon: Rocket,
        title: "Doorvoeren",
        body: "We begeleiden de verandering in gerichte stappen, terwijl je bedrijf gewoon doordraait. Waar software helpt — van het Clarke-platform tot maatwerk — bouwen en koppelen we het zelf.",
    },
    {
        n: "04",
        icon: Anchor,
        title: "Borgen",
        body: "Daarna meten we het resultaat, sturen we bij en blijven we beschikbaar. Verbetering die alleen op papier bestaat, is geen verbetering.",
    },
];

const TARIEVEN = [
    {
        icon: Clock,
        title: "Advies op uurbasis",
        body: "Voor een scherpe blik op één vraagstuk: een proces, een investering, een knel punt. Je betaalt alleen voor de uren die je nodig hebt.",
        note: "Uurtarief in overleg",
    },
    {
        icon: Route,
        title: "Vast verbetertraject",
        body: "Voor grotere verbeteringen: van verkenning tot doorvoering, met vooraf een vaste prijs en een duidelijke planning. Je weet precies waar je aan toe bent.",
        note: "Vaste prijs in overleg",
    },
    {
        icon: Blocks,
        title: "Het Clarke Platform",
        body: "Onze eigen software voor planning, CRM, facturatie en productiedata — het gereedschap waarmee advies structureel wordt. Ook los af te nemen.",
        note: "Abonnement, prijs in overleg",
    },
];

const TRAJECTEN = [
    {
        tag: "Voorbeeldtraject",
        title: "Maakbedrijf, 12 medewerkers",
        body: "Roosteren en offertes opvolgen kostten de eigenaar twee dagen per week. We brachten het complete orderproces in kaart, richtten het opnieuw in en zetten het Clarke-platform in. Het roosteren en offreren kost nu uren in plaats van dagen.",
    },
    {
        tag: "Voorbeeldtraject",
        title: "Foodproducent, eigen recepturen",
        body: "Batch-registratie en HACCP-documentatie gebeurden op papier en in losse spreadsheets. We digitaliseerden de receptuur- en batchketen, zodat tracering en kostprijs nu per order zichtbaar zijn.",
    },
    {
        tag: "Voorbeeldtraject",
        title: "Zakelijke dienstverlener, groeiend team",
        body: "Groei zorgde voor rommelige overdracht en vergeten leads. We stroomlijnden het verkoop- en opvolgproces en koppelde de boekhouding — één overzicht van lead tot factuur.",
    },
];

export default function ConsultancyPage() {
    useEffect(() => {
        document.title = "Consultancy — Clarke Business Solutions";
        return () => {
            document.title = "Clarke Business Solutions — Consultancy & Software";
        };
    }, []);

    return (
        <div data-testid="consultancy-page" className="min-h-screen bg-ink text-bone">
            <div className="noise-overlay" aria-hidden="true" />
            <header className="border-b border-white/10">
                <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-5">
                    <BrandLockup emblemClass="h-12 w-12" textClass="text-xl" align="start" />
                    <a
                        data-testid="consultancy-back-link"
                        href="/"
                        className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar site
                    </a>
                </div>
            </header>

            <main className="mx-auto max-w-[1100px] px-6 py-16 md:py-24">
                <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Consultancy</p>
                <h1 className="mt-4 max-w-3xl font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
                    Advies over bedrijfsvoering, <span className="italic text-gold">uitvoerbaar</span> vanaf dag één.
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-ash">
                    Clarke Business Solutions helpt ondernemingen met bedrijfsvoering, procesoptimalisatie en
                    bedrijfsontwikkeling. Geen dikke rapporten, maar praktisch advies dat je direct kunt uitvoeren —
                    en waar het helpt, bouwen wij de software die het advies structureel maakt.
                </p>
                <div className="mt-12 overflow-hidden border border-white/10">
                    <img
                        src={PRESENTATIE_IMG}
                        alt="Clarke Business Solutions presentatie"
                        className="aspect-[3/2] w-full object-cover md:aspect-[21/9]"
                    />
                </div>

                <section data-testid="consultancy-aanpak" className="mt-20">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-gold">De aanpak</p>
                    <div className="mt-3"><H2>Vier stappen, één richting</H2></div>
                    <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
                        {AANPAK.map((s) => (
                            <div
                                key={s.n}
                                data-testid={`consultancy-stap-${s.n}`}
                                className="border border-white/10 bg-coal p-8 transition-colors duration-500 hover:border-gold/40"
                            >
                                <div className="flex items-center justify-between">
                                    <s.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                    <span className="font-display text-sm text-ash/60">{s.n}</span>
                                </div>
                                <h3 className="mt-8 font-display text-2xl tracking-tight text-bone">{s.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-ash">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section data-testid="consultancy-tarieven" className="mt-24">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Tarieven</p>
                    <div className="mt-3"><H2>Duidelijk vooraf, altijd in overleg</H2></div>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ash">
                        Elk bedrijf en elk vraagstuk is anders. Daarom werken we niet met een prijslijst maar met
                        een kennismaking zonder verplichtingen: daarna weet je precies wat het kost en wat het oplevert.
                    </p>
                    <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {TARIEVEN.map((tar) => (
                            <div
                                key={tar.title}
                                data-testid={`consultancy-tarief-${tar.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="flex flex-col border border-white/10 bg-coal p-8 transition-colors duration-500 hover:border-gold/40"
                            >
                                <tar.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                <h3 className="mt-8 font-display text-xl tracking-tight text-bone">{tar.title}</h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-ash">{tar.body}</p>
                                <p className="mt-6 border-t border-white/10 pt-4 text-[11px] uppercase tracking-[0.25em] text-gold">
                                    {tar.note}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                <section data-testid="consultancy-trajecten" className="mt-24">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Typische trajecten</p>
                    <div className="mt-3"><H2>Hoe dat er in de praktijk uitziet</H2></div>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ash">
                        Drie veelvoorkomende situaties waarin wij ondernemers begeleiden — geen bestaande klanten,
                        maar herkenbare voorbeelden van trajecten zoals wij die aanpakken.
                    </p>
                    <div className="mt-10 space-y-6">
                        {TRAJECTEN.map((tr) => (
                            <div
                                key={tr.title}
                                data-testid={`consultancy-traject-${tr.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="grid grid-cols-1 gap-4 border border-white/10 bg-coal p-8 md:grid-cols-12 md:items-center"
                            >
                                <div className="md:col-span-4">
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold/70">{tr.tag}</p>
                                    <h3 className="mt-2 font-display text-xl tracking-tight text-bone">{tr.title}</h3>
                                </div>
                                <p className="text-sm leading-relaxed text-ash md:col-span-8">{tr.body}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section data-testid="consultancy-cta" className="mt-24 border border-gold/30 bg-gold/5 p-10 text-center">
                    <h2 className="font-display text-3xl tracking-tight text-bone">Benieuwd wat het jou oplevert?</h2>
                    <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-ash">
                        Vertel ons waar de druk zit. Binnen één werkdag reageren we — en na één gesprek weet je of
                        en hoe wij je verder kunnen helpen.
                    </p>
                    <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                        <a
                            data-testid="consultancy-cta-contact"
                            href="/#contact"
                            className="group flex items-center gap-2 bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                        >
                            Start een gesprek
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </a>
                        <a
                            data-testid="consultancy-cta-email"
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="border border-white/15 px-8 py-4 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            {CONTACT_EMAIL}
                        </a>
                    </div>
                </section>

                <div className="mt-16 border-t border-white/10 pt-8 text-[11px] uppercase tracking-[0.25em] text-ash">
                    <span data-testid="consultancy-company-details" className="normal-case tracking-normal text-ash/70">
                        Clarke Business Solutions · KvK 42207779 · SBI 70202, 62100 & 82400
                    </span>
                </div>
            </main>
        </div>
    );
}
